import { GLContext } from 'nanogl/types'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import GltfTypes from 'nanogl-gltf/lib/types/GltfTypes'
import Node from 'nanogl-node'
import Chunk from 'nanogl-pbr/Chunk'
import ChunksSlots from 'nanogl-pbr/ChunksSlots'
import Input, { ShaderType, Uniform } from 'nanogl-pbr/Input'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { RenderContext } from '@/webgl/core/RenderContext'
import gui from '@/webgl/dev/gui'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import MeshRendererProxy from '@/webgl/runner/rendering/MeshRendererProxy'
import { backgroundCutout } from '@/webgl/runner/rendering/RenderingUtils'

const RAIL_PATH = 'caris_2/rails.glb'

class ClipChunk extends Chunk {
  private clip: Input
  public clipUniform: Uniform
  get ClipMin() {
    return this.clipUniform.value[0]
  }
  set ClipMin(v) {
    this.clipUniform.value[0] = v
  }

  get ClipMax() {
    return this.clipUniform.value[1]
  }
  set ClipMax(v) {
    this.clipUniform.value[1] = v
  }

  constructor(readonly gl: GLContext) {
    super(true, true)
    this.addChild((this.clip = new Input('Clip', 2, ShaderType.FRAGMENT)))
    this.clipUniform = this.clip.attachUniform('uClip', 2)
  }

  protected _genCode(slots: ChunksSlots): void {
    slots.add(
      'pv',
      /*glsl*/ `
      OUT vec3 vWorldPosition;
    `
    )

    slots.add(
      'postv',
      /*glsl*/ `
      vWorldPosition = vertex.worldPos;
      `
    )

    slots.add(
      'pf',
      /*glsl*/ `
        IN vec3 vWorldPosition;
      `
    )

    slots.add(
      'postf',
      /* glsl */ `
      if(!gl_FrontFacing) FragColor.rgb = vec3(0.,0.161,0.22);
      if(vWorldPosition.z < Clip().x) discard;
      if(vWorldPosition.z > Clip().y) discard;
    `
    )
  }
}

export default class RailsRenderer implements ILoadable {
  gltf: Gltf
  active: boolean = false
  node: Node

  chunk: ClipChunk
  proxy1: MeshRendererProxy
  proxy2: MeshRendererProxy

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.node = new Node()
    this.chunk = new ClipChunk(this.gl)
    this.chunk.ClipMin = -10.1
    this.chunk.ClipMax = 0
  }

  setActive(flag: boolean) {
    this.active = flag
    if (this.active) {
      this.world.root.add(this.node)
    } else {
      this.world.root.remove(this.node)
    }
  }

  async load(abort: AbortSignal): Promise<void> {
    if (!this.active) return Promise.resolve()

    gui.add(this.chunk, 'ClipMin', { min: -12, max: -2 })
    gui.add(this.chunk, 'ClipMax', { min: -12, max: -2 })

    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()
    overrides.overridePass('', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.glconfig.enableCullface(false)
      pass.inputs.add(this.chunk)
      backgroundCutout(this.gl, pass)
      return pass
    })

    this.gltf = await new GltfResource(RAIL_PATH, this.gl, { extensions: [overrides] }).load(abort)

    const rail = this.gltf.getElementByName(GltfTypes.NODE, 'rail').renderable
    this.proxy1 = new MeshRendererProxy(this.gltf, rail.mesh)
    this.proxy2 = new MeshRendererProxy(this.gltf, rail.mesh)
    this.proxy1.node.position.set(rail.node.position)
    this.proxy2.node.position.set(rail.node.position)

    this.proxy1.node.invalidate()
    this.proxy2.node.invalidate()

    this.node.add(this.proxy1.node)
    this.node.add(this.proxy2.node)

    this.proxy1.node.z = 0
    this.proxy2.node.z = -this.world.settings.LANE_LENGTH * 0.95
  }

  public preRender(time: TimeEntity) {
    if (!this.active) return

    const settings = this.world.settings
    const deltaTime = time.unscaledDeltaTime
    const advance = deltaTime * settings.scrollSpeed

    this.proxy1.node.z += advance
    this.proxy2.node.z += advance

    if (this.proxy1.node.z > settings.LANE_LENGTH * 0.95) this.proxy1.node.z = -settings.LANE_LENGTH * 0.95
    if (this.proxy2.node.z > settings.LANE_LENGTH * 0.95) this.proxy2.node.z = -settings.LANE_LENGTH * 0.95
  }

  render(context: RenderContext) {
    if (!this.active) return

    this.proxy1.render(context)
    this.proxy2.render(context)
  }
}
